/**
 * Playing a {@link Schedule} through an audio engine.
 *
 * The player schedules a window of the piece at a time rather than all of it at once. An
 * orchestral score is hundreds of thousands of notes, and asking a browser to hold an
 * oscillator for every one of them at the moment play is pressed stalls the page; a few
 * seconds ahead is enough for the engine's clock to stay exact and costs nothing.
 *
 * Nothing here starts a timer. {@link Player.advance} is called by whoever is already
 * animating -- a `requestAnimationFrame` loop that is moving the cursor on the page
 * anyway -- which keeps this package free of any platform's idea of time. A caller that
 * forgets to call it hears the first window and then silence, which is why `advance` is
 * the one method the interface documents as required rather than optional.
 *
 * The sound itself is in `tone.ts`, which is the one voice everything in this package is
 * heard through -- the player, the metronome and the echo of a key played on a keyboard.
 */

import type { Click } from "./click";
import { countInSeconds } from "./click";
import type { TimeRange } from "./locate";
import type { PlayedNote, Schedule } from "./schedule";
import type { AudioEngine, PlayedOn, SoundingTone, SwellPoint } from "./port";
import { gainOf } from "./loudness";
import { hertz, sound } from "./tone";

export interface PlayerOptions {
  /** Oscillator shape. `"triangle"` is quiet enough to hear a chord in. */
  readonly wave?: string;
  /** Seconds of ramp up and down, which is what keeps a note from clicking. */
  readonly attack?: number;
  readonly release?: number;
  /** Loudness of the whole player, 0 to 1. */
  readonly volume?: number;
  /** Seconds scheduled ahead of the clock. */
  readonly lookahead?: number;
  /** The longest a drum sounds, however long it is written. */
  readonly strikeLength?: number;
  /**
   * The metronome, from {@link clickTrack}. Empty for none.
   *
   * It is passed in rather than derived here because what is clicked is a decision -- the
   * count-in, whether repeats are followed -- and this file's job is to make sounds at
   * times it is given.
   */
  readonly clicks?: readonly Click[];
  /** Pitch of a click, in hertz. The downbeat sounds at {@link accentHertz}. */
  readonly clickHertz?: number;
  readonly accentHertz?: number;
  /** How long a click lasts and how loud it is, 0 to 1. */
  readonly clickLength?: number;
  readonly clickVolume?: number;
}

export interface Player {
  /**
   * Play from `seconds` into the piece, or from where it was paused.
   *
   * A negative position is the count-in, and is allowed exactly as far back as the clicks
   * given go: {@link start} is where playing from the top begins when one was asked for.
   */
  play(seconds?: number): void;
  /** The earliest position the player stands at: below zero when there is a count-in. */
  start(): number;
  /**
   * Play a stretch over and over, or stop looping with `null`.
   *
   * Setting a loop while the music is outside it does not jump: the loop takes hold the
   * next time the playhead reaches its end, which is what lets a passage be marked while it
   * is already playing.
   */
  setLoop(range: TimeRange | null): void;
  loop(): TimeRange | null;
  /** Stop, keeping the position. */
  pause(): void;
  /** Stop and return to the beginning. */
  stop(): void;
  playing(): boolean;
  /** Seconds into the piece, now. */
  position(): number;
  /** Put the playhead at a position, whether or not the music is running. */
  seek(seconds: number): void;
  /** Schedule whatever is now within the lookahead. Call this while playing. */
  advance(): void;
  /** The notes sounding at a moment, for a cursor or a highlight. */
  soundingAt(seconds: number): readonly PlayedNote[];
}

const DEFAULTS = {
  wave: "triangle",
  attack: 0.008,
  release: 0.06,
  volume: 0.25,
  lookahead: 0.5,
  strikeLength: 0.12,
  clicks: [] as readonly Click[],
  clickHertz: 1000,
  accentHertz: 1600,
  clickLength: 0.03,
  clickVolume: 0.3,
} as const;

/** A player for one schedule. Load another by making another player. */
export function createPlayer(
  engine: AudioEngine,
  schedule: Schedule,
  options: PlayerOptions = {},
): Player {
  const settings = { ...DEFAULTS, ...options };
  const notes = [...schedule.notes].sort((a, b) => a.start - b.start);
  const clicks = [...settings.clicks].sort((a, b) => a.start - b.start);
  const earliest = -countInSeconds(clicks);

  let origin = 0;
  let offset = earliest;
  let running = false;
  let next = 0;
  let nextClick = 0;
  let looping: TimeRange | null = null;
  let sounding: SoundingTone[] = [];

  const position = (): number => (running ? engine.currentTime - origin : offset);

  const silence = (): void => {
    for (const voice of sounding) voice.stop(engine.currentTime);
    sounding = [];
  };

  /** One tone, kept hold of so that stopping the player can cut it short. */
  const tone = (
    hz: number,
    at: number,
    length: number,
    peak: number,
    played: PlayedOn | null = null,
    swell: readonly SwellPoint[] = [],
  ): void => {
    sounding.push(sound(engine, settings, hz, at, length, peak, played, swell));
  };

  const strike = (note: PlayedNote, at: number): void => {
    // A drum is struck and stops. Holding it for the length it is written would turn a
    // bar of quavers on a closed hi-hat into one continuous sound, which is the one thing
    // a player listening for the rhythm cannot hear past.
    const length = note.percussion ? Math.min(note.duration, settings.strikeLength) : note.duration;
    // A drum's key names a drum rather than a pitch, so it is never asked of a recording of
    // a pitched instrument: doing so would fetch whatever note happened to sit at that key.
    const played = note.percussion
      ? null
      : {
          program: note.program,
          plucked: note.plucked,
          muted: note.muted,
          bridge: note.bridge,
          midi: note.midi,
          velocity: note.velocity,
        };
    // A struck drum dies away on its own, so it has no loudness to move while it is held.
    const swell = note.percussion
      ? []
      : note.swell.map((point) => ({
          at: at + point.at * length,
          peak: gainOf(point.velocity) * settings.volume,
          velocity: point.velocity,
        }));
    tone(hertz(note.midi), at, length, gainOf(note.velocity) * settings.volume, played, swell);
  };

  /** Where in each list to resume from, for a position the playhead has just been put at. */
  const cue = (at: number): void => {
    const found = notes.findIndex((note) => note.start + note.duration > at);
    next = found < 0 ? notes.length : found;
    const beat = clicks.findIndex((click) => click.start >= at);
    nextClick = beat < 0 ? clicks.length : beat;
  };

  /** Put the playhead at a position, whether or not the music is running. */
  const seek = (at: number): void => {
    silence();
    offset = Math.max(earliest, at);
    if (running) origin = engine.currentTime - offset;
    cue(offset);
  };

  const advance = (): void => {
    if (!running) return;
    const now = position();
    if (looping !== null && now >= looping.to) {
      seek(looping.from);
      return;
    }

    const limit = looping === null ? Number.POSITIVE_INFINITY : looping.to;
    const horizon = Math.min(now + settings.lookahead, limit);

    while (next < notes.length) {
      const note = notes[next];
      if (note === undefined || note.start > horizon) break;
      next += 1;
      // A note the playhead has already passed the end of is not started mid-way; it is
      // simply missed, which is what seeking into the middle of a long note has to mean
      // when the engine can only begin a tone at its beginning.
      if (note.start + note.duration <= now) continue;
      strike(note, Math.max(engine.currentTime, origin + note.start));
    }

    while (nextClick < clicks.length) {
      const click = clicks[nextClick];
      if (click === undefined || click.start > horizon) break;
      nextClick += 1;
      if (click.start < now) continue;
      const hz = click.accent ? settings.accentHertz : settings.clickHertz;
      tone(
        hz,
        Math.max(engine.currentTime, origin + click.start),
        settings.clickLength,
        settings.clickVolume,
      );
    }

    const clock = engine.currentTime;
    sounding = sounding.filter((voice) => voice.until > clock);
  };

  return {
    play(seconds?: number): void {
      if (running) return;
      offset = Math.max(earliest, seconds ?? offset);
      origin = engine.currentTime - offset;
      cue(offset);
      running = true;
      void engine.resume();
      advance();
    },
    pause(): void {
      if (!running) return;
      offset = position();
      running = false;
      silence();
    },
    stop(): void {
      running = false;
      offset = earliest;
      next = 0;
      nextClick = 0;
      silence();
    },
    start: () => earliest,
    setLoop(range: TimeRange | null): void {
      looping = range === null || range.to <= range.from ? null : range;
    },
    loop: () => looping,
    playing: () => running,
    position,
    seek,
    advance,
    soundingAt: (seconds: number) =>
      notes.filter((note) => note.start <= seconds && note.start + note.duration > seconds),
  };
}
